import type { ScoringMethod } from './types';

function buildAnswerMap(
  options: string[],
  selected: string[],
): Record<string, { selected: boolean }> {
  return Object.fromEntries(
    options.map((key) => [key, { selected: selected.includes(key) }]),
  );
}

function binaryScore(selected: string[], correct: string[]): number {
  const sortedSelected = [...selected].sort();
  const sortedCorrect = [...correct].sort();

  if (sortedSelected.length !== sortedCorrect.length) {
    return 0;
  }

  for (let i = 0; i < sortedSelected.length; i++) {
    if (sortedSelected[i] !== sortedCorrect[i]) {
      return 0;
    }
  }

  return 1;
}

function pointPerCorrectAnswer(
  options: string[],
  correct: string[],
  selected: string[],
): number {
  const answerMap = buildAnswerMap(options, selected);
  return options.reduce((out, option) => {
    if (
      (answerMap[option].selected && correct.includes(option)) ||
      (!answerMap[option].selected && !correct.includes(option))
    ) {
      return out + 1;
    }
    return out;
  }, 0);
}

function deductForWrongAnswer(
  options: string[],
  correct: string[],
  selected: string[],
): number {
  // D-752-07: every option is judged on whether its checked/unchecked state matches ground
  // truth, same as POINT_PER_CORRECT_ANSWER — a correctly-left-unchecked wrong answer earns a
  // point, same as everywhere else in the app. The "deduct" in this method's name is the extra
  // penalty layer: a mismatch costs -1 instead of just missing out on 0.
  const answerMap = buildAnswerMap(options, selected);
  const result = options.reduce((out, option) => {
    const match = answerMap[option].selected === correct.includes(option);
    return match ? out + 1 : out - 1;
  }, 0);

  return Math.max(0, result);
}

export function calculateScore(
  options: string[],
  correct: string[],
  selected: string[],
  method: ScoringMethod,
): { score: number; maxScore: number } {
  if (method === 'BINARY') {
    return { score: binaryScore(selected, correct), maxScore: 1 };
  }

  if (method === 'DEDUCT_FOR_WRONG_ANSWER') {
    return { score: deductForWrongAnswer(options, correct, selected), maxScore: options.length };
  }

  return { score: pointPerCorrectAnswer(options, correct, selected), maxScore: options.length };
}

export function calculateOptionScores(
  options: string[],
  correct: string[],
  selected: string[],
  method: ScoringMethod,
): Record<string, number> {
  return Object.fromEntries(
    options.map((option) => {
      const isSelected = selected.includes(option);
      const isCorrect = correct.includes(option);
      const match = isSelected === isCorrect;

      if (method === 'BINARY') {
        return [option, match ? 1 : 0];
      }

      if (method === 'DEDUCT_FOR_WRONG_ANSWER') {
        // D-752-07: same match-state rule as BINARY/POINT_PER_CORRECT_ANSWER, but a mismatch
        // costs -1 instead of scoring 0 — that penalty is what "deduct for wrong answer" means.
        return [option, match ? 1 : -1];
      }

      // POINT_PER_CORRECT_ANSWER
      return [option, match ? 1 : 0];
    }),
  );
}
